import { ChangeJobFairPubsub } from 'app/app-events';
import { useAppContext } from 'app/context/app-context';
import { useAuth } from 'app/context/auth-context';
import { RxSubscription } from 'app/RxSubscriptions';
import { ActiveBoothViewCounterState } from 'app/services/BoothService';
import { VJFPCache } from 'app/services/CacheUtils';
// import { ActiveInterviewSessionState, InterviewSessionService } from 'app/services/InterviewSessionService';
import { TimeService } from 'app/services/TimeService';
import { JobFair, JobFairStatus, Roles } from 'app/types';
import { config } from 'config';
import { useCallback, useEffect, useState } from 'react';
import { parse } from 'zipson';

function useWebSocket (): boolean | null {
  const [isSocketConnected, setIsSocketConnected] = useState<boolean | null>(null);
  const { user } = useAuth();
  const { jobFair } = useAppContext();

  // Wrap handleWebSocketOpen inside useCallback
  const handleWebSocketOpen = useCallback(async (ws: WebSocket) => {
    setIsSocketConnected(true);

    if (!user?.id || !jobFair?.id || !user?.organizationId) {
      console.log('user.id is null, so reloading the page');
      window.location.reload();
      return;
    }

    ws.send(`{"action": "setUserId", "userId": "${user.id}", "jobFairId": "${jobFair.id}" , "organizationId": "${user.organizationId}"}`); ;

    if (jobFair?.status === JobFairStatus.Started || jobFair?.status === JobFairStatus.ClosingSession) {
      let boothId: string | null = null;
      if (user?.roles?.includes(Roles.Recruiter)) {
        boothId = window.sessionStorage.getItem('recruiterBoothId');
      }

      const matrix = JSON.stringify({
        action: 'loginCount',
        userId: user?.id.toString(),
        jobFairId: jobFair.id,
        boothId,
        role: user?.roles?.toString()
      });
      ws.send(matrix);
    }

    RxSubscription.getBackendSubscriptionObservable().subscribe(subscriptionObj => {
      if (!subscriptionObj) return;
      const props = JSON.stringify(subscriptionObj.props);
      if (ws.readyState === ws.OPEN) { ws.send(`{"action": "${subscriptionObj.command}", "subscriptionName": "${subscriptionObj.subscriptionName}", "props": ${props}, "userId": "${user?.id}", "jobFairId": "${jobFair?.id}", "organizationId": "${user?.organizationId}" }`); }
    });

    if (ws.readyState === 1 && user.roles) {
      if (user.roles.includes(Roles.Applicant)) { ws.send(`{"action": "ping", "userId": "${user.id}", "jobFairId": "${jobFair.id}", "role": "Applicant"}`); } else if (user.roles.includes(Roles.Recruiter)) { ws.send(`{"action": "ping", "userId": "${user.id}", "jobFairId": "${jobFair.id}", "role": "Recruiter", "boothId": "${window.sessionStorage.getItem('recruiterBoothId') || 0}" }`); }
    }
  }, [user?.id, jobFair?.id]);

  const handleWebSocketMessage = useCallback(async (event: MessageEvent) => {
    const data = event.data;
    let resp: ({action?: string; value: any; timeStamp: string; interviewInvitePending: boolean; boothViewNeedRefreshCounter: number, failedPosts: any})|undefined;
    try {
      resp = JSON.parse(data);
    } catch (err) {
      console.error(err);
    }
    if (!resp) return;

    const timeStamp = resp.timeStamp;
    if (resp.action?.startsWith('on')) { RxSubscription.notify(resp.action, resp.value); }

    if (resp.failedPosts) {
      const values = parse(resp.failedPosts);
      if (values) {
        for (const value of values) {
          RxSubscription.notify(value.subscriptionName, value);
        }
      }
    }

    if (resp.boothViewNeedRefreshCounter) {
      console.log('boothViewNeedRefreshCounter = ' + resp.boothViewNeedRefreshCounter);
      const currentCounter = ActiveBoothViewCounterState.getCurrentBoothViewCounter();
      if (resp.boothViewNeedRefreshCounter !== currentCounter) {
        ActiveBoothViewCounterState.setBoothViewCounter(Number(resp.boothViewNeedRefreshCounter));
      }
    }

    // if (resp.interviewInvitePending && jobFair?.id && user?.id && user.roles?.includes(Roles.Applicant)) {
    //   const interviewSessionService = new InterviewSessionService();
    //   if (!ActiveInterviewSessionState.getCurrentActiveInterviewSession()) {
    //     interviewSessionService.getActiveInterviewByJobseeker(jobFair.id, user.id).then(interviewSessions => {
    //       if (interviewSessions?.length && interviewSessions[0]) {
    //         ActiveInterviewSessionState.setInterviewSession(interviewSessions[0]);
    //         ChangeInterviewSessionPubsub.onAmplifyData({
    //           mode: 'create',
    //           value: interviewSessions[0]
    //         });
    //       }
    //     });
    //   }
    // }

    localStorage.setItem('Ws_TimeStamp', timeStamp);
    if (localStorage.getItem('DeltaTimeDiff')) {
      await new TimeService().loadDelta();
    }

    if (timeStamp && jobFair) {
      ChangeJobFairPubsub.publish({
        mode: 'update',
        value: jobFair
      });
    };

    // Handle Booth List version update scenario

    if (jobFair) { _hanldeBoothListVersion(jobFair, resp); }
  }, [jobFair]);

  useEffect(() => {
    if (!user || !jobFair?.id) return;

    const ws = new WebSocket(config.socketUrl);

    ws.onopen = () => handleWebSocketOpen(ws); // Pass the WebSocket instance

    ws.onmessage = handleWebSocketMessage;

    ws.onclose = () => {
      setIsSocketConnected(false);
    };

    ws.onerror = (err) => {
      setIsSocketConnected(false);
      console.error('Socket encountered error: ', JSON.stringify(err), 'Closing socket');
      ws.close();
      window.location.reload();
    };

    // Handle cleanup before page unload
    window.onbeforeunload = () => {
      if (ws) {
        ws.onclose = null; // Disable onclose handler
        if (ws.readyState <= 1) {
          ws.close();
        }
      }
    };

    const intervalId = setInterval(() => {
      if (ws && ws.readyState === 1 && user.roles) {
        if (user.roles.includes(Roles.Applicant)) { ws.send(`{"action": "ping", "userId": "${user.id}", "jobFairId": "${jobFair.id}", "role": "Applicant"}`); } else if (user.roles.includes(Roles.Recruiter)) { ws.send(`{"action": "ping", "userId": "${user.id}", "jobFairId": "${jobFair.id}", "role": "Recruiter", "boothId": "${window.sessionStorage.getItem('recruiterBoothId') || 0}"}`); }
      }
    }
    , 10000); // 10 seconds in milliseconds

    return () => {
      clearInterval(intervalId);
      ws.close();
      window.onbeforeunload = null;
    };
  }, [user, jobFair?.id]);

  return isSocketConnected;
}

export default useWebSocket;

function _hanldeBoothListVersion (jobFair: JobFair, resp: any) {
  const localBoothListVersionKey = `localBoothListVersion_${jobFair.id}`;
  const localBoothListVersionValue = window.sessionStorage.getItem(localBoothListVersionKey);
  if (localBoothListVersionValue && Number(localBoothListVersionValue) !== Number(resp.boothListVersion)) {
    // Clear Cache if local booth version do not match with updated one
    VJFPCache.removeItem(`BOOTHS_${jobFair.id}`);
    // update local booth version with the updated one
    window.sessionStorage.setItem(localBoothListVersionKey, resp.boothListVersion);
  } else if (localBoothListVersionValue == null) {
    // Set the local booth version with Booth List version if localBoothVersion is not set
    window.sessionStorage.setItem(localBoothListVersionKey, resp.boothListVersion);
  }
}
